/**
 * What is actually different about playing this kingdom.
 *
 * ── Why this file exists at all ─────────────────────────────────────────────
 *
 * The whole point of the race layer was that the peoples be *structurally*
 * different — the undead pay for a host in Mana rather than in beds, the Silver
 * Reaches may not reinforce, the Splitmoot has no population cap and can be too
 * large. A selection screen that answers that with a portrait and a paragraph of
 * flavour throws the entire design away at the last step, because the player
 * makes the decision on the screen, not in the source.
 *
 * So the screen does not describe a kingdom. It *derives* one, out of the same
 * data the simulation reads, and every number on it is folded by the simulation's
 * own function. `menu.ts` lays this out and adds nothing to it; the judgement
 * about what is worth saying lives here, where it can be tested without a DOM.
 *
 * ── The four questions, in the order a new player asks them ─────────────────
 *
 *   1. What stops me raising one more company?   the *binding*, and it is the
 *      highest-leverage difference in the game because it decides what the
 *      answer to "I want a bigger army" is, and therefore what the middle of a
 *      run is about
 *   2. What is on my resource strip that is not on anybody else's, and what is
 *      missing?                                   the economy mask
 *   3. What do the numbers actually come out at?  the folded bag
 *   4. What can I build that nobody else can?     the race's own two buildings
 *      and its fourth Academy column
 *
 * ── Honesty, which is a feature here and not a caveat ───────────────────────
 *
 * One of the eight bindings is still declared and enforced by nothing — `crews`,
 * the Cog-Warrens' machine toll — and the other seven are built. A screen that
 * promised a player "there is no population cap" and then capped them would be
 * worse than one that said nothing, so `bindingEnforced` reads the same fact the
 * simulation does and the screen says so in one quiet line. Six of those lines
 * have now disappeared on their own, which is the whole argument for the
 * mechanism.
 */

import { BINDING_NAMES, KIN_NAMES, RACES, race, raceEconomyMask, type RaceDef } from '../data/races.ts';
import { LEADERS, leader, leadersOfRace, type Leader } from '../data/leaders.ts';
import { buildingTypeById, type BuildingType } from '../data/buildings.ts';
import { techFor } from '../data/tech.ts';
import { faction } from '../data/factions.ts';
import { HERO_CLASSES } from '../data/heroes.ts';
import { RESOURCE_KEYS, RESOURCE_LABELS, type ResourceKey } from '../sim/types.ts';
import { applyModBag, defaultMods, type Mods } from '../sim/world.ts';
import { MOD_LABELS } from '../sim/systems/items.ts';

/**
 * A kingdom, which is a house on a people.
 *
 * `Cmd.PickKingdom` takes exactly this id — it resolves a leader id to the race
 * *and* the house, which is why the command is one command rather than two. The
 * race and the character are consequences of the kingdom, and a screen that
 * offered them as siblings could offer a combination the world does not contain.
 */
export interface Kingdom {
  /** The `Leader` id, and the payload of `Cmd.PickKingdom`. */
  id: string;
  /** "The Splitmoot of Loudest Gribb" — the kingdom, not the people. */
  name: string;
  leader: Leader;
  race: RaceDef;
}

/**
 * Every kingdom on offer, in roster order, which is kin order.
 *
 * Two houses per race today. When `worldgen` seats kingdoms on a board this
 * function is what changes: it becomes a read off the generated world rather
 * than off the whole roster, and nothing downstream of it moves.
 */
export function kingdoms(): Kingdom[] {
  return LEADERS.map((l) => ({
    id: l.id,
    name: `${race(l.raceId).name} of ${l.title} ${l.name}`,
    leader: l,
    race: race(l.raceId),
  }));
}

/** The kin headings the rail groups under, in roster order and without empties. */
export function kinOrder(): Array<{ kin: string; label: string }> {
  const seen: string[] = [];
  for (const r of RACES) if (!seen.includes(r.kin)) seen.push(r.kin);
  return seen.map((k) => ({ kin: k, label: KIN_NAMES[k as keyof typeof KIN_NAMES] ?? k }));
}

export function kingdomsOfKin(kin: string): Kingdom[] {
  return kingdoms().filter((k) => k.race.kin === kin);
}

export function kingdom(id: string): Kingdom {
  const l = leader(id) ?? LEADERS[0];
  return { id: l.id, name: `${race(l.raceId).name} of ${l.title} ${l.name}`, leader: l, race: race(l.raceId) };
}

/**
 * Is this people's binding actually enforced by the simulation yet?
 *
 * `systems/companies.ts::musterRoom` now forks on `mana`, `names`, `thralls` and
 * `moot` and falls through to housing for the rest; `groves` is enforced a layer
 * up in `recomputePopCap`, which is what `musterRoom` measures against, and
 * `irreplaceable` in `reinforceCompany`. So seven of the eight are real and
 * **`crews` is the one that is not** — the Cog-Warrens play as a housed people
 * until the machine toll lands, and the quiet line on their card says so.
 *
 * Still a hand-kept list, which is the smallest lie available: parsing the
 * `structural` strings would be brittle and asking the sim would need a world.
 * It is checked by `tests/newgame.test.ts`, which fails the day `musterRoom`
 * learns another binding — and did, which is how this list got updated.
 */
const ENFORCED_BINDINGS = ['housing', 'mana', 'irreplaceable', 'names', 'groves', 'thralls', 'moot'];

export function bindingEnforced(raceId: string): boolean {
  return ENFORCED_BINDINGS.includes(race(raceId).binding);
}

/** One line of the folded-numbers table. */
export interface ModLine {
  key: string;
  label: string;
  /** "+15%" or "+60", already signed and formatted. */
  value: string;
  /** Whether this is better or worse than the baseline, for colour. */
  good: boolean;
}

/**
 * Keys where a smaller number is the better one. Without this, "Build cost
 * −6%" is painted as a loss and "Siege damage taken +40%" as a gain, which is
 * exactly backwards on the two lines a player is most likely to read.
 */
const LOWER_IS_BETTER = ['buildCost', 'powerCooldown', 'grainUpkeep', 'siegeResist', 'reinforceCost'];

function pct(v: number): string {
  const n = (v - 1) * 100;
  return `${n >= 0 ? '+' : '−'}${Math.abs(n) < 10 ? Math.abs(n).toFixed(1) : Math.round(Math.abs(n))}%`;
}

function flat(v: number): string {
  return `${v >= 0 ? '+' : '−'}${Math.abs(v) < 10 ? Math.abs(v).toFixed(2) : Math.round(Math.abs(v))}`;
}

/**
 * The numbers this kingdom actually starts with, folded the way `initRun` folds
 * them.
 *
 * Race passives first and then the house's — the *same order* `foldIdentity`
 * uses, and it matters for the same reason: both layers multiply into one bag
 * and floating-point multiplication is not associative. Calling the simulation's
 * own `applyModBag` rather than reimplementing the multiply/add decision is the
 * whole reason this can be trusted; the bag knows which keys multiply and this
 * file deliberately does not.
 */
export function foldedMods(raceId: string, leaderId: string): Mods {
  const mods = defaultMods();
  for (const rp of race(raceId).passives) if (rp.mods) applyModBag(mods, rp.mods);
  const house = leaderId ? leader(leaderId) : undefined;
  for (const lp of house?.passives ?? []) if (lp.mods) applyModBag(mods, lp.mods);
  return mods;
}

/** Every modifier this kingdom moves off the baseline, biggest departure first. */
export function modLines(raceId: string, leaderId: string): ModLine[] {
  const base = defaultMods() as unknown as Record<string, number>;
  const now = foldedMods(raceId, leaderId) as unknown as Record<string, number>;
  const out: ModLine[] = [];
  for (const k of Object.keys(base)) {
    if (now[k] === base[k]) continue;
    const mult = base[k] === 1;
    const up = now[k] > base[k];
    out.push({
      key: k,
      label: MOD_LABELS[k as keyof Mods] ?? k,
      value: mult ? pct(now[k]) : flat(now[k] - base[k]),
      good: LOWER_IS_BETTER.includes(k) ? !up : up,
    });
  }
  // Biggest departure from the baseline first, so the line that changes the game
  // most is the line at the top rather than the one whose key sorts earliest.
  return out.sort((a, b) => magnitude(b, base, now) - magnitude(a, base, now));
}

function magnitude(l: ModLine, base: Record<string, number>, now: Record<string, number>): number {
  return base[l.key] === 1 ? Math.abs(now[l.key] - 1) : Math.abs(now[l.key] - base[l.key]) / 60;
}

/** A resource key with its player-facing name, for the larder row. */
export interface LarderKey {
  key: ResourceKey;
  label: string;
}

export interface Larder {
  /** Keys this people has that the common list does not give everybody. */
  grants: LarderKey[];
  /** Keys of the common set this people simply does not have. */
  denies: LarderKey[];
  reapsSouls: boolean;
}

export function larder(raceId: string): Larder {
  const r = race(raceId);
  const mask = raceEconomyMask(raceId, RESOURCE_KEYS);
  const name = (k: ResourceKey): LarderKey => ({ key: k, label: RESOURCE_LABELS[k] ?? k });
  return {
    // Read back off the mask rather than off `economy.grants` directly, so the
    // screen and the storage clamp cannot disagree about what a people holds.
    grants: r.economy.grants.filter((k) => mask.has(k)).map(name),
    denies: r.economy.denies.map(name),
    reapsSouls: r.economy.reapsSouls,
  };
}

/**
 * The whole sheet for one kingdom.
 *
 * Everything the selection screen shows, derived rather than written down, so a
 * race added next month appears on this screen the day it is added and a race
 * whose passives change says something different the same afternoon.
 */
export interface KingdomSheet {
  kingdom: Kingdom;
  kinLabel: string;
  /** Where this people belongs on a generated world. The `worldgen` seam. */
  homeland: string;

  /** What stops you raising one more company. The headline difference. */
  binding: { name: string; text: string; enforced: boolean };
  larder: Larder;
  /** Race layer first, then house layer. Labelled as two layers, never merged. */
  racePassives: RaceDef['passives'];
  housePassives: Leader['passives'];
  mods: ModLine[];

  /** The two buildings only this people may raise. */
  buildings: BuildingType[];
  academy: { name: string; flavour: string; nodes: number };

  /** The hero class this kingdom's leader fights and levels as. */
  archetype: { id: string; name: string; epithet: string; passive: string };
  /** Whose banner, roster and architecture this kingdom carries. */
  bannerFactionId: string;
  /** Unit ids this kingdom fields that nobody else does. Empty for every race today. */
  ownUnits: string[];
  /** How many companies its roster runs to — shared with its faction, for now. */
  rosterSize: number;
}

export function kingdomSheet(id: string): KingdomSheet {
  const k = kingdom(id);
  const r = k.race;
  const l = k.leader;
  const hero = HERO_CLASSES.find((h) => h.id === l.archetype);
  const f = faction(r.factionId);
  return {
    kingdom: k,
    kinLabel: KIN_NAMES[r.kin] ?? r.kin,
    homeland: r.homeland,
    binding: { name: BINDING_NAMES[r.binding], text: r.bindingText, enforced: bindingEnforced(r.id) },
    larder: larder(r.id),
    racePassives: r.passives,
    housePassives: l.passives,
    mods: modLines(r.id, l.id),
    buildings: r.buildings.map(buildingTypeById),
    academy: {
      name: r.branchName,
      flavour: r.branchFlavour,
      // `techFor` is the shared column plus this race's own; the race's own is
      // the difference, and the difference is what the screen is claiming.
      nodes: techFor(r.id).filter((t) => t.race === r.id).length,
    },
    archetype: {
      id: l.archetype,
      name: hero?.name ?? l.archetype,
      epithet: hero?.epithet ?? '',
      passive: hero ? `${hero.startingPassive.name} — ${hero.startingPassive.rules}` : '',
    },
    bannerFactionId: f.id,
    ownUnits: [...r.units],
    rosterSize: f.roster.length,
  };
}

/**
 * The one-line answer to "what would be different about playing this one",
 * short enough to sit under a 44px thumbnail in the rail.
 *
 * The binding is the honest answer nine times out of eleven, because it is the
 * thing that changes the *shape* of a run rather than its numbers. Where two
 * peoples share a binding — four of them are on beds and wages — the tie is
 * broken by whatever else is structural about them, so no two rows in the rail
 * ever read the same.
 */
export function railLine(raceId: string): string {
  const r = race(raceId);
  if (r.binding !== 'housing') return BINDING_NAMES[r.binding];
  if (r.economy.denies.length > 0) return `No ${r.economy.denies.map((k) => RESOURCE_LABELS[k]).join(', ').toLowerCase()}`;
  if (r.id === 'moonbound') return 'Cannot store';
  if (r.economy.grants.length > 0) return RESOURCE_LABELS[r.economy.grants[0]];
  return BINDING_NAMES[r.binding];
}

/**
 * Two houses of one people, and how many of the five identity levers they
 * actually differ in. Used by the screen to say *why* a second kingdom of a race
 * is a second kingdom and not a recolour.
 */
export function siblingHouses(raceId: string, exceptId: string): Leader[] {
  return leadersOfRace(raceId).filter((l) => l.id !== exceptId);
}
